import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import Button from '@sorare/core/src/atoms/buttons/Button';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { DisplayS, LabelL, LabelM } from '@sorare/core/src/atoms/typography';
import { Arrows } from '@sorare/core/src/components/rivals/Seasons/SeasonLeaderboardList/Arrows';
import { useIsMobile } from '@sorare/core/src/hooks/device/useIsMobile';
import { glossary } from '@sorare/core/src/lib/glossary';

import { RewardBoxDialog } from '@sorare/gameplay/src/components/rewards/RewardBoxDialog';

import { BaseLayout } from '@sorare/football/src/components/rivals/SquadPromotionRelegationStory/BaseLayout';
import first from '@sorare/football/src/components/rivals/seasons/SeasonLeaderboard/SeasonLeaderboardHeader/PodiumAvatar/assets/1st.png';

import { Promoted_footballRivalsSquadPromotionRelegationStory } from './__generated__/index.graphql';

const RankImage = styled(Vertical).attrs({ center: true })`
  position: relative;
  width: 200px;
  height: 217px;
  background: center/cover url(${first});
`;
const SquadImage = styled.img`
  position: absolute;
  top: 47%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: -1;
  clip-path: inset(40px 40px 40px 40px);
  width: 90%;
`;
const SquadRank = styled(LabelL).attrs({ bold: true })`
  position: absolute;
  bottom: 38px;
  z-index: 1;
`;
const DivisionBadge = styled.img`
  width: 80px;
  height: 80px;
  position: absolute;
  bottom: -50px;
  z-index: 1;
`;

type Props = {
  story: Promoted_footballRivalsSquadPromotionRelegationStory;
  onDismiss: () => void;
};
export const Promoted = ({ story, onDismiss }: Props) => {
  const isMobile = useIsMobile();
  const [rewardDialogOpened, setRewardDialogOpened] = useState(false);

  return (
    <>
      <BaseLayout
        diamondColor="var(--c-rgb-rivals-purple-600)"
        gridColor="#ad00ff"
        titleColor="var(--c-rivals-purple-600)"
        secondDiamonWrapper
        title={
          <Horizontal gap={2}>
            <Arrows color="var(--c-rivals-purple-600)" />
            <DisplayS>
              <FormattedMessage
                id="SquadPromotionRelegationStory.Promoted.title"
                defaultMessage="Congratulations!"
              />
            </DisplayS>
            <Arrows color="var(--c-rivals-purple-600)" />
          </Horizontal>
        }
        subtitle={
          <LabelM color="var(--c-neutral-700)">
            <FormattedMessage
              id="SquadPromotionRelegationStory.Promoted.subtitle"
              defaultMessage="You've been Promoted"
            />
          </LabelM>
        }
        content={
          <RankImage>
            <SquadImage src={story.squad.logo?.pictureUrl} alt="" />
            <SquadRank>
              <FormattedMessage {...glossary.ordinal} values={{ ordinal: 1 }} />
            </SquadRank>
            <DivisionBadge src={story.newDivision?.badgeUrl} alt="" />
          </RankImage>
        }
        footer={
          story.rewardProbabilisticBundle
            ? {
                color: '#2D003D',
                content: (
                  <Horizontal>
                    <LabelM>
                      <FormattedMessage
                        id="SquadPromotionRelegationStory.Promoted.footer"
                        defaultMessage="You won a reward box!"
                      />
                    </LabelM>
                    <img
                      width={24}
                      src={story.rewardProbabilisticBundle?.config.pictureUrl}
                      alt=""
                    />
                  </Horizontal>
                ),
              }
            : undefined
        }
        cta={
          <Button
            onClick={() => {
              if (story.rewardProbabilisticBundle) {
                setRewardDialogOpened(true);
              } else onDismiss();
            }}
            color="secondary"
            size="medium"
            fullWidth={isMobile}
          >
            <FormattedMessage
              {...(story.rewardProbabilisticBundle
                ? glossary.claim
                : glossary.continue)}
            />
          </Button>
        }
      />
      {story.rewardProbabilisticBundle && (
        <RewardBoxDialog
          open={rewardDialogOpened}
          rewardBoxes={[story.rewardProbabilisticBundle]}
          onClose={() => {
            setRewardDialogOpened(false);
            onDismiss();
          }}
        />
      )}
    </>
  );
};
Promoted.fragments = {
  footballRivalsSquadPromotionRelegationStory: gql`
    fragment Promoted_footballRivalsSquadPromotionRelegationStory on FootballRivalsSquadPromotionRelegationStory {
      id
      newDivision {
        id
        badgeUrl
      }
      squad {
        slug
        logo {
          id
          pictureUrl
        }
      }
      rewardProbabilisticBundle {
        id
        config {
          id
          pictureUrl
        }
        ...RewardBoxDialog_ProbabilisticBundle
      }
    }
    ${RewardBoxDialog.fragments.ProbabilisticBundle}
  ` as TypedDocumentNode<Promoted_footballRivalsSquadPromotionRelegationStory>,
};
